
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { TextField, Select, MenuItem, Button, Box, Paper, Fade, Collapse, IconButton, Tooltip, Chip, Stack } from '@mui/material';

import { updateFilters } from '../../redux/transactionSlice';
import { loadAccount } from '../../redux/financeAccountSlice';
import { loadCategory } from '../../redux/categorySlice';
import { loadGroup } from '../../redux/groupSlice';
import { FormControl, InputLabel } from '@mui/material';
import { dateToString } from '../../shared/formatters';

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
}));

const FilterHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
    },
}));

const ToggleButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: 'rgb(0,98,105)',
    color: 'white',
    boxShadow: '0 4px 16px rgba(0, 98, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: 'rgb(0,85,91)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
    },
    [theme.breakpoints.down('sm')]: {
        flex: 1,
    },
}));

const QuickActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'white',
    color: theme.palette.text.primary,
    border: '2px solid #e0e0e0',
    width: '48px',
    height: '48px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        borderColor: 'rgb(0,98,105)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
}));

const FilterPanel = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: theme.spacing(2),
}));

const CollapsibleWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    zIndex: 10,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 'auto',
        marginTop: theme.spacing(2),
    },
}));

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '180px',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        width: '100%',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '48px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginTop: theme.spacing(1),
    }
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        width: '100%',
    }
}));

const FilterPillsContainer = styled(Stack)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1),
    minHeight: '32px',
}));

const FilterPill = styled(Chip)(({ theme }) => ({
    borderRadius: '20px',
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
    color: 'rgb(0, 98, 105)',
    fontWeight: 500,
    border: '1px solid rgba(0, 98, 105, 0.3)',
    height: 'auto',
    '& .MuiChip-deleteIcon': {
        color: 'rgba(0, 98, 105, 0.6)',
        '&:hover': {
            color: 'rgb(0, 98, 105)',
        },
    },
}));

function submitSearch(event, searchFilters, updateFilters, setIsFilterOpen) {
    event.preventDefault();
    updateFilters(searchFilters);
    setIsFilterOpen(false);
};

function updateUserFilter(updateSearchFilters, searchFilters, users, accounts) {
    const financeAccounts = (accounts || []).filter(account => users.includes(account.user.id)).map(account => account.id);

    updateSearchFilters({ ...searchFilters, users, financeAccounts });
};

function updateGroupFilter(updateSearchFilters, searchFilters, selectedGroups, categories) {
    const filteredCategories = selectedGroups.length > 0 
        ? (categories || [])
            .filter(category => category.group && selectedGroups.includes(category.group.id))
            .map(category => category.id)
        : [];

    updateSearchFilters({ ...searchFilters, groups: selectedGroups, categories: filteredCategories });
};

function resetFilters(event, updateFilters, updateSearchFilters, setIsFilterOpen) {
    event.preventDefault();
    var today = new Date();
    const searchFilters = {
        description: '',
        categories: [],
        groups: [],
        financeAccounts: [],
        users: [],
        from: dateToString(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0))
    };
    updateSearchFilters(searchFilters);
    updateFilters(searchFilters);
    setIsFilterOpen(false);
}

function getFilterPills(searchFilters, accounts, categories, groups, users) {
    const pills = [];

    // Add description pill
    if (searchFilters.description) {
        pills.push({
            id: `desc-${searchFilters.description}`,
            label: `Descrição: ${searchFilters.description}`,
            type: 'description',
            value: searchFilters.description
        });
    }

    // Add date range pill
    if (searchFilters.from || searchFilters.to) {
        pills.push({
            id: 'date-range',
            label: `${searchFilters.from} até ${searchFilters.to}`,
            type: 'dateRange',
            value: { from: searchFilters.from, to: searchFilters.to }
        });
    }

    // Add group pills
    searchFilters.groups.forEach(groupId => {
        const group = (groups || []).find(g => g.id === groupId);
        if (group) {
            pills.push({
                id: `group-${groupId}`,
                label: `Agrupamento: ${group.name}`,
                type: 'group',
                value: groupId
            });
        }
    });

    // Add category pills only if no groups are selected
    if (searchFilters.groups.length === 0) {
        searchFilters.categories.forEach(categoryId => {
            const category = (categories || []).find(c => c.id === categoryId);
            if (category) {
                pills.push({
                    id: `category-${categoryId}`,
                    label: `Categoria: ${category.name}`,
                    type: 'category',
                    value: categoryId
                });
            }
        });
    }

    // Add user pills
    searchFilters.users.forEach(userId => {
        const user = (users || []).find(u => u.id === userId);
        if (user) {
            pills.push({
                id: `user-${userId}`,
                label: `Responsável: ${user.name}`,
                type: 'user',
                value: userId
            });
        }
    });

    // Add finance account pills
    searchFilters.financeAccounts.forEach(accountId => {
        const account = (accounts || []).find(a => a.id === accountId);
        if (account) {
            pills.push({
                id: `account-${accountId}`,
                label: `Conta: ${account.name}`,
                type: 'account',
                value: accountId
            });
        }
    });

    return pills;
}

function handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction, categories) {
    let updatedFilters = { ...searchFilters };
    
    switch (pill.type) {
        case 'description':
            updatedFilters = { ...updatedFilters, description: '' };
            break;
        case 'dateRange':
            var today = new Date();
            updatedFilters = {
                ...updatedFilters,
                from: dateToString(new Date(today.getFullYear(), today.getMonth(), 1)),
                to: dateToString(new Date(today.getFullYear(), today.getMonth() + 1, 0))
            };
            break;
        case 'group':
            // Remove the group and all categories belonging to that group
            const remainingGroups = updatedFilters.groups.filter(g => g !== pill.value);
            const remainingCategories = updatedFilters.categories.filter(catId => {
                const category = (categories || []).find(c => c.id === catId);
                return !category || !category.group || remainingGroups.includes(category.group.id);
            });
            updatedFilters = {
                ...updatedFilters,
                groups: remainingGroups,
                categories: remainingCategories
            };
            break;
        case 'category':
            updatedFilters = {
                ...updatedFilters,
                categories: updatedFilters.categories.filter(c => c !== pill.value)
            };
            break;
        case 'user':
            updatedFilters = {
                ...updatedFilters,
                users: updatedFilters.users.filter(u => u !== pill.value),
                financeAccounts: updatedFilters.financeAccounts
            };
            break;
        case 'account':
            updatedFilters = {
                ...updatedFilters,
                financeAccounts: updatedFilters.financeAccounts.filter(a => a !== pill.value)
            };
            break;
        default:
            break;
    }
    
    updateSearchFilters(updatedFilters);
    updateFiltersAction(updatedFilters);
}

function Filter(props) {
    const dispatch = useDispatch();
    
    // Redux state selectors
    const filterFields = useSelector(state => state.transaction.filterFields);
    const accounts = useSelector(state => state.financeAccount.items);
    const shouldUpdateAccounts = useSelector(state => state.financeAccount.shouldBeUpdated);
    const categories = useSelector(state => state.category.items);
    const shouldUpdateCategories = useSelector(state => state.category.shouldBeUpdated);
    const groups = useSelector(state => state.group.items);
    const shouldUpdateGroups = useSelector(state => state.group.shouldBeUpdated);
    const users = useSelector(state => state.account.users);
    const onExport = props.onExport;
    
    // Redux actions
    const updateFiltersAction = (filters) => dispatch(updateFilters(filters));

    const [searchFilters, updateSearchFilters] = useState({ ...filterFields });
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useEffect(() => {
        if (shouldUpdateAccounts) {
            dispatch(loadAccount());
        }
    }, [shouldUpdateAccounts, dispatch]);

    useEffect(() => {
        if (shouldUpdateCategories) {
            dispatch(loadCategory());
        }
    }, [shouldUpdateCategories, dispatch]);

    useEffect(() => {
        if (shouldUpdateGroups) {
            dispatch(loadGroup());
        }
    }, [shouldUpdateGroups, dispatch]);

    const filterPills = getFilterPills(searchFilters, accounts, categories, groups, users);
    const hasActiveFilters = filterPills.length > 0;

    const filterForm = (
        <Fade in={true} timeout={300}>
            <FilterPanel elevation={0}>
                <StyledForm onSubmit={event => submitSearch(event, searchFilters, updateFiltersAction, setIsFilterOpen)}>
                    <StyledFormControl>
                        <StyledTextField
                            id="from"
                            name="from"
                            label="Início"
                            type="date"
                            variant="outlined"
                            autoFocus
                            value={searchFilters.from}
                            onChange={event => updateSearchFilters({ ...searchFilters, from: event.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </StyledFormControl>
                    <StyledFormControl>
                        <StyledTextField
                            id="to"
                            name="to"
                            label="Fim"
                            type="date"
                            variant="outlined"
                            value={searchFilters.to}
                            onChange={event => updateSearchFilters({ ...searchFilters, to: event.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </StyledFormControl>
                    <StyledFormControl>
                        <StyledTextField
                            id="description"
                            name="description"
                            label="Descrição"
                            variant="outlined"
                            value={searchFilters.description}
                            onChange={event => updateSearchFilters({ ...searchFilters, description: event.target.value })}
                            placeholder="Digite a descrição para buscar..."
                        />
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="groups-label">Agrupamento</InputLabel>
                        <StyledSelect
                            labelId="groups-label"
                            id="groups"
                            multiple
                            value={searchFilters.groups}
                            label="Agrupamento"
                            onChange={event => updateGroupFilter(updateSearchFilters, searchFilters, event.target.value, categories)}
                        >
                            {(groups || []).map(group => <MenuItem key={group.id} value={group.id}>{`${group.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="categories-label">Categoria</InputLabel>
                        <StyledSelect
                            labelId="categories-label"
                            id="categories"
                            multiple
                            value={searchFilters.categories}
                            label="Categoria"
                            onChange={event => updateSearchFilters({ ...searchFilters, categories: event.target.value })}
                        >
                            {(categories || [])
                                .filter(category => searchFilters.groups.length === 0 || (category.group && searchFilters.groups.includes(category.group.id)))
                                .map(category => <MenuItem key={category.id} value={category.id}>{`${category.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="users-label">Responsável</InputLabel>
                        <StyledSelect
                            labelId="users-label"
                            id="userId"
                            multiple
                            value={searchFilters.users}
                            label="Responsável"
                            onChange={event => updateUserFilter(updateSearchFilters, searchFilters, event.target.value, accounts)}
                        >
                            {(users || []).map(user => <MenuItem key={user.id} value={user.id}>{`${user.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="accounts-label">Conta</InputLabel>
                        <StyledSelect
                            labelId="accounts-label"
                            id="accounts"
                            multiple
                            value={searchFilters.financeAccounts}
                            label="Conta"
                            onChange={event => updateSearchFilters({ ...searchFilters, financeAccounts: event.target.value })}
                        >
                            {(accounts || []).map(account => <MenuItem key={account.id} value={account.id}>{`${account.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    
                    <ButtonGroup>
                        <PrimaryButton
                            type="submit"
                            variant="contained"
                            startIcon={<FilterListIcon />}
                        >
                            Filtrar
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={event => resetFilters(event, updateFiltersAction, updateSearchFilters, setIsFilterOpen)}
                        >
                            Limpar
                        </SecondaryButton>
                    </ButtonGroup>
                </StyledForm>
            </FilterPanel>
        </Fade>
    );

    return (
        <FilterContainer>
            <FilterHeader>
                <ToggleButton
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    startIcon={<FilterListIcon />}
                    endIcon={isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isFilterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </ToggleButton>
                <Tooltip title="Limpar Filtros" placement="top">
                    <QuickActionButton
                        onClick={event => resetFilters(event, updateFiltersAction, updateSearchFilters, setIsFilterOpen)}
                    >
                        <ClearIcon />
                    </QuickActionButton>
                </Tooltip>
                <Tooltip title="Exportar" placement="top">
                    <QuickActionButton
                        onClick={event => onExport()}
                    >
                        <FileDownloadIcon />
                    </QuickActionButton>
                </Tooltip>
            </FilterHeader>
            
            {hasActiveFilters && (
                <FilterPillsContainer role="region" aria-label="Applied filters">
                    {filterPills.map(pill => (
                        <FilterPill
                            key={pill.id}
                            label={pill.label}
                            onDelete={() => handleRemoveFilterPill(pill, searchFilters, updateSearchFilters, updateFiltersAction, categories)}
                            sx={{
                                py: 1.5,
                                px: 1.5,
                            }}
                        />
                    ))}
                </FilterPillsContainer>
            )}
            
            <CollapsibleWrapper>
                <Collapse in={isFilterOpen} timeout={300}>
                    {filterForm}
                </Collapse>
            </CollapsibleWrapper>
        </FilterContainer>
    );
}

export default Filter;